import type { AuthHeaders } from '../client'
import { request, requestRaw } from '../client'
import type {
  KitColorSelection,
  MatchSheet,
  MatchSheetAssignPlayer,
  MatchSheetNumberChange,
  MatchSheetSetRole,
  MatchSheetSubmissionSuccess,
  MatchesResponse,
  ScoreLinkRequest,
  ScoreMatchesResponse,
  StaffSelection,
  SubmissionDiff,
} from '../types'

export function listMatches(
  auth: AuthHeaders,
  range: { from: string; to: string },
): Promise<MatchesResponse> {
  return request<MatchesResponse>('/matches', {
    query: { from: range.from, to: range.to },
    auth,
  })
}

export function getMatchSheet(
  auth: AuthHeaders,
  matchId: number,
): Promise<MatchSheet> {
  return request<MatchSheet>(`/matches/${matchId}/sheet`, { auth })
}

export function assignPlayerToSheet(
  auth: AuthHeaders,
  matchId: number,
  change: MatchSheetAssignPlayer,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/matches/${matchId}/sheet`, {
    method: 'PATCH',
    body: change,
    auth,
  })
}

export function setSheetKit(
  auth: AuthHeaders,
  matchId: number,
  kitId: string,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/matches/${matchId}/sheet/kit`, {
    method: 'PATCH',
    body: { kit_id: kitId },
    auth,
  })
}

export function setSheetKitColors(
  auth: AuthHeaders,
  matchId: number,
  colors: KitColorSelection,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/matches/${matchId}/sheet/kit-colors`, {
    method: 'PATCH',
    body: colors,
    auth,
  })
}

/** Sets (or clears, with `kitId=null`) the independent goalkeeper kit. */
export function setSheetGkKit(
  auth: AuthHeaders,
  matchId: number,
  kitId: string | null,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/matches/${matchId}/sheet/gk-kit`, {
    method: 'PATCH',
    body: { kit_id: kitId },
    auth,
  })
}

export function setSheetGkKitColors(
  auth: AuthHeaders,
  matchId: number,
  colors: KitColorSelection,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/matches/${matchId}/sheet/gk-kit-colors`, {
    method: 'PATCH',
    body: colors,
    auth,
  })
}

export function setSheetRole(
  auth: AuthHeaders,
  matchId: number,
  change: MatchSheetSetRole,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/matches/${matchId}/sheet/role`, {
    method: 'PATCH',
    body: change,
    auth,
  })
}

export function setSheetNumbers(
  auth: AuthHeaders,
  matchId: number,
  changes: MatchSheetNumberChange[],
): Promise<{ ok: true; changed: number }> {
  return request<{ ok: true; changed: number }>(
    `/matches/${matchId}/sheet/numbers`,
    {
      method: 'PATCH',
      body: changes,
      auth,
    },
  )
}

export function setSheetStaff(
  auth: AuthHeaders,
  matchId: number,
  selections: StaffSelection[],
): Promise<{ ok: true; count: number }> {
  return request<{ ok: true; count: number }>(
    `/matches/${matchId}/sheet/staff`,
    {
      method: 'PATCH',
      body: selections,
      auth,
    },
  )
}

export function copyPreviousStaff(
  auth: AuthHeaders,
  matchId: number,
): Promise<{
  ok: boolean
  source: { match_id: number; date: string | null; opponent: string }
  staff_selections: StaffSelection[]
}> {
  return request(`/matches/${matchId}/sheet/staff/copy-previous`, {
    method: 'POST',
    auth,
  })
}

export function linkScoreMatch(
  auth: AuthHeaders,
  matchId: number,
  body: ScoreLinkRequest,
): Promise<{ match_id: number; score_match_id: number }> {
  return request(`/matches/${matchId}/score-link`, {
    method: 'POST',
    body,
    auth,
  })
}

export function unlinkScoreMatch(
  auth: AuthHeaders,
  matchId: number,
): Promise<{ match_id: number; unlinked: true }> {
  return request(`/matches/${matchId}/score-link`, {
    method: 'DELETE',
    auth,
  })
}

export function saveSheetToScore(
  auth: AuthHeaders,
  matchId: number,
): Promise<MatchSheetSubmissionSuccess> {
  return request(`/matches/${matchId}/sheet/save-to-score`, {
    method: 'POST',
    auth,
  })
}

export function submitSheetToScore(
  auth: AuthHeaders,
  matchId: number,
): Promise<MatchSheetSubmissionSuccess> {
  return request(`/matches/${matchId}/sheet/submit-to-score`, {
    method: 'POST',
    auth,
  })
}

export function reverifyScoreSubmission(
  auth: AuthHeaders,
  matchId: number,
): Promise<{
  ok: true
  club_match_sheet_id: number
  submission_diff: SubmissionDiff | null
  submission_diff_error: string | null
}> {
  return request(`/matches/${matchId}/sheet/reverify`, {
    method: 'POST',
    auth,
  })
}

export function checkScoreSubmitted(
  auth: AuthHeaders,
  matchId: number,
): Promise<{ submitted: boolean; club_match_sheet_id: number | null }> {
  return request(`/matches/${matchId}/sheet/submitted`, { auth })
}

export function refreshScoreSubmissions(
  auth: AuthHeaders,
): Promise<{ checked: number; newly_submitted: number[] }> {
  return request(`/matches/sheets/refresh-submissions`, {
    method: 'POST',
    auth,
  })
}

export async function fetchScoreReport(
  auth: AuthHeaders,
  clubMatchSheetId: number,
): Promise<Blob> {
  const response = await requestRaw(`/score/match-sheet/report`, {
    query: { club_match_sheet_id: clubMatchSheetId },
    auth,
  })
  return await response.blob()
}

export function listScoreMatches(
  auth: AuthHeaders,
): Promise<ScoreMatchesResponse> {
  return request<ScoreMatchesResponse>('/sync/matches/score', {
    method: 'POST',
    auth,
  })
}
